import {
  CredentialsAndIdentityId,
  CredentialsAndIdentityIdProvider,
  GetCredentialsOptions,
} from '@aws-amplify/core';
import logger from 'src/utils/logger'
import { Subscription } from 'rxjs';
import {
  CognitoAWSCredentialsAndIdentityIdProvider,
  cognitoCredentialsProvider,
} from 'aws-amplify/auth/cognito';
import { reachabilityMonitor } from '@alucio/core';

class BeaconCredentialsAndIdentityIdProvider implements CredentialsAndIdentityIdProvider {
  // We always need to return some credential values when offline otherwise
  // amplify will initiate a logout. When offline we won't be making requests anyways
  static DUMMY_CREDENTIALS: CredentialsAndIdentityId = {
    credentials: {
      accessKeyId: 'OFFLINE',
      secretAccessKey: 'OFFLINE',
      sessionToken: 'OFFLINE',
      expiration: new Date(new Date().getTime() + 1000),
    },
    identityId: 'OFFLINE',
  };

  static LOCK_NAME: string = 'BEACON_ID_PROVIDER_LOCK'

  cognitoProvider: CognitoAWSCredentialsAndIdentityIdProvider
  isOnline: boolean
  cachedCredentials: CredentialsAndIdentityId | undefined
  reachabilitySubscription: Subscription

  constructor() {
    this.cognitoProvider = cognitoCredentialsProvider;
    this.isOnline = navigator.onLine;
    this.cachedCredentials = BeaconCredentialsAndIdentityIdProvider.DUMMY_CREDENTIALS;
    this.reachabilitySubscription = reachabilityMonitor.subscribe(v => {
      if (this.isOnline !== v.online) { this.isOnline = v.online }
    })
  }

  /**
   * Retrieves AWS credentials and identity ID with iOS PWA lock timeout protection.
   *
   * This method addresses the same iOS PWA issue as BeaconCognitoTokenProvider (BEAC-7353)
   * where iPad PWA users couldn't log in after logout due to navigator.locks getting stuck.
   *
   * The solution implements a 3-second timeout using AbortController to prevent
   * indefinite lock waiting, with retry logic that steals the lock if needed.
   *
   * @param getCredentialsOptions - Options for credential retrieval
   * @param retryCount - Current retry attempt (used internally for lock stealing)
   * @returns Promise resolving to credentials and identity ID or undefined
   * @throws Error if lock acquisition fails after max retries
   */
  async getCredentialsAndIdentityId(getCredentialsOptions: GetCredentialsOptions, retryCount: number = 0):
    Promise<CredentialsAndIdentityId | undefined> {
    logger.auth.signIn.authenticator.debug(`Credential Fetch!! ${this.isOnline}`)
    if (this.isOnline) {
      // iOS PWA Lock Timeout Protection (BEAC-7353)
      // Prevents indefinite lock waiting that blocks credential requests after logout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3 * 1000); // 3 second timeout

      try {
        // Use lock to prevent concurrent credential requests and leverage caching
        // The timeout ensures iOS PWA doesn't get stuck waiting for lock release
        this.cachedCredentials = await navigator.locks.request(
          BeaconCredentialsAndIdentityIdProvider.LOCK_NAME,
          { mode: 'exclusive', signal: controller.signal },
          async () => {
            return await this.cognitoProvider.getCredentialsAndIdentityId(getCredentialsOptions)
          },
        );

        // Clear timeout since operation completed successfully
        clearTimeout(timeoutId);
        return this.cachedCredentials
      } catch (error) {
        // Clear timeout in all error cases
        clearTimeout(timeoutId);

        if (error instanceof Error && error.name === 'AbortError') {
          // Lock timeout occurred - attempt to steal the lock and retry
          const maxRetries = 3;
          if (retryCount >= maxRetries) {
            logger.auth.signIn.authenticator.error(`Failed to acquire lock after ${maxRetries} attempts`);
            throw new Error(`Failed to acquire credentials lock after ${maxRetries} attempts`);
          }

          // Force release the stuck lock by stealing it
          logger.auth.signIn.authenticator.warn(`Stealing lock (attempt ${retryCount + 1}/${maxRetries})`)
          await navigator.locks.request(
            BeaconCredentialsAndIdentityIdProvider.LOCK_NAME,
            { mode: 'exclusive', steal: true }, async () => {
              logger.auth.signIn.authenticator.warn('Lock released')
            },
          )

          // Retry credential acquisition with incremented retry count
          return this.getCredentialsAndIdentityId(getCredentialsOptions, retryCount + 1)
        }
        throw error
      }
    } else {
      return this.cachedCredentials
    }
  }

  clearCredentialsAndIdentityId(): void {
    this.cognitoProvider.clearCredentialsAndIdentityId()
    this.cachedCredentials = BeaconCredentialsAndIdentityIdProvider.DUMMY_CREDENTIALS
  }
}
const beaconCredentialsAndIdentityIdProvider = new BeaconCredentialsAndIdentityIdProvider()
export { beaconCredentialsAndIdentityIdProvider }
